import React, { useState, useEffect } from "react";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";

const MyMessages = ({ setTotalMessages }) => {
  const headers = [
    "Selection",
    "Action",
    "Message ID",
    "Subject",
    "Sender",
    "Recipient",
    "Date Sent",
    "Date Received",
    "Message Type",
    "Priority",
    "Status",
    "Category",
    "Attachments",
    "Related Order",
    "Department",
    "Response Required",
    "Follow-up Date",
    "Message Source",
    "Tags",
    "Last Updated",
    "Archived"
  ];

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(15);

  useEffect(() => {
    // Dummy data - match structure to headers
    const dummyMessages = Array.from({ length: 50 }, (_, i) => ({
      "Message ID": `MSG-${1000 + i}`,
      "Subject": `Order Update #${i + 1}`,
      "Sender": `sender${i}@company.com`,
      "Recipient": `recipient${i}@client.com`,
      "Date Sent": new Date().toLocaleString(),
      "Date Received": new Date().toLocaleString(),
      "Message Type": ["Email", "Notification", "Alert"][i % 3],
      "Priority": ["Low", "Medium", "High"][i % 3],
      "Status": ["Unread", "Read", "Archived"][i % 3],
      "Category": ["Order", "Shipping", "Payment"][i % 3],
      "Attachments": i % 4 === 0 ? "Yes" : "No",
      "Related Order": `ORD-${Math.floor(Math.random() * 1000)}`,
      "Department": ["Sales", "Support", "Logistics"][i % 3],
      "Response Required": i % 5 === 0 ? "Yes" : "No",
      "Follow-up Date": new Date(Date.now() + 86400000 * i).toLocaleDateString(),
      "Message Source": ["Web", "Mobile", "API"][i % 3],
      "Tags": ["Urgent", "Follow-up", "Internal"][i % 3],
      "Last Updated": new Date().toLocaleString(),
      "Archived": i % 10 === 0 ? "Yes" : "No"
    }));

    setMessages(dummyMessages);
    setTotalMessages(dummyMessages.length);
    setIsLoading(false);
  }, [setTotalMessages]);

  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = messages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(messages.length / messagesPerPage);

  const handleSelectAll = (checked) => {
    setSelectedMessages(checked 
      ? currentMessages.map(m => m["Message ID"]) 
      : []
    );
  };

  const handleSingleCheckbox = (messageId) => {
    setSelectedMessages(prev => prev.includes(messageId)
      ? prev.filter(id => id !== messageId)
      : [...prev, messageId]
    );
  };

  const handleNextPage = () => currentPage < totalPages && setCurrentPage(p => p + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(p => p - 1);

  return (
    <div className="bg-white flex flex-col h-full">
      {isLoading ? (
        <Loader 
          color="text-blue-600" 
          size="h-5 w-5"
          message="Loading Messages...."
        />
      ) : (
        <>
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto">
            <Table 
              headers={headers} 
              data={currentMessages}
              onSelectAll={handleSelectAll}
            >
              {(row) => (
                <>
                  <td
                    className="sticky left-0 bg-white border-b border-gray-200"
                    style={{
                      width: "80px",
                      minWidth: "80px",
                      maxWidth: "80px",
                      boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                    }}
                  >
                    <input 
                      type="checkbox" 
                      className="cursor-pointer mx-auto block"
                      checked={selectedMessages.includes(row["Message ID"])}
                      onChange={() => handleSingleCheckbox(row["Message ID"])}
                    />
                  </td>

                  <td
                    className="sticky left-[80px] bg-white border-b border-gray-200 z-[190] text-center"
                    style={{
                      width: "50px",
                      minWidth: "50px",
                      maxWidth: "50px",
                      boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                    }}
                  >
                    <button 
                      className="p-1 text-gray-500 hover:text-gray-800"
                      onClick={() => console.log("Action for:", row["Message ID"])}
                    >
                      <MoreHorizontal size={18} />
                    </button>
                  </td>

                  {headers.slice(2).map((header) => (
                  <td
                    key={header}
                    className="px-2 sm:px-3 py-1 sm:py-2 text-black text-xs sm:text-sm md:text-base border-b border-gray-200"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "200px"  // ADD EXPLICIT WIDTH
                    }}
                  >
                    {row[header]}
                  </td>
                ))}
                </>
              )}
            </Table>
          </div>

          <div className="flex justify-between border-t items-center bg-white p-fluid-1">
            <div className="flex items-center">
              <span className="mr-2">Messages per page</span>
              <select
                value={messagesPerPage}
                onChange={(e) => {
                  setMessagesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border-b-2 border-black focus:outline-none"
              >
                {[10,15,20, 25].map((num) => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="mx-2 text-gray-500 disabled:opacity-50"
              >
                ❮
              </button>
              <span>
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="mx-2 text-gray-500 disabled:opacity-50"
              >
                ❯
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MyMessages;